import { formatMarkdown } from "@/components/formatMarkdown";
import { ExternalLink } from "@/components/OptionalLink";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import Link from "next/link";

interface FAQProps {
  question: string;
  answer: string;
}

export const faqs: FAQProps[] = [
  // --- TOP 5: Shown on landing page ---
  {
    question: "I built my app with AI (Cursor, Claude, etc.). Can DollarDeploy get it live?",
    answer:
      "That's exactly what we're built for. Connect your GitHub repo, and DollarDeploy auto-detects your stack, builds your app, configures HTTPS, and deploys it to a server—all without you touching a terminal. Our AI agent can also set things up for you if you describe what you need in plain language. You don't need to know what Nginx, systemd, or Docker are."
  },
  {
    question: "How is this different from Vercel, Netlify, or Heroku?",
    answer:
      "Those platforms charge per request, per seat, or per function—and lock you into proprietary runtimes you can't take elsewhere. DollarDeploy deploys to servers *you own* (a $4/month Hetzner VPS, DigitalOcean, or any Linux server). You get the same push-to-deploy experience with automatic HTTPS, monitoring, and backups—at a fraction of the cost, with no vendor lock-in, and a built-in AI agent that manages infrastructure for you."
  },
  {
    question: "What does the AI agent actually do?",
    answer:
      "It takes real actions on your infrastructure—not just gives advice. You can tell it to deploy an app, provision a new server, install a database, or troubleshoot a production error, all in plain language. The agent understands your apps, servers, and configuration, and executes commands directly. Think of it as a DevOps engineer that works instantly and never sleeps."
  },
  {
    question: "How much does it cost?",
    answer:
      "DollarDeploy starts at **$1/app/month**. You also pay for your own server—a capable VPS costs as little as **$4/month on Hetzner**. So a full production setup with HTTPS, monitoring, logs, and backups can run for under **$9/month**. No per-request fees, no bandwidth overages, no surprise bills. For comparison, Vercel starts at $20/month and usage charges can reach $600+."
  },
  {
    question: "What apps and frameworks can I deploy?",
    answer:
      "Next.js, React, Node.js, Python, Go, Rust, Java, PHP—and anything else via Docker Compose. Your apps run natively as system services (no Docker overhead), so they're fast and lightweight. We auto-detect your framework and configure the build automatically."
  },
  // --- EXTENDED: Shown on /faq page ---
  {
    question: "Do I need to know DevOps, Linux, or Docker?",
    answer:
      "No. DollarDeploy handles server configuration, Nginx, HTTPS certificates, builds, deployments, and monitoring automatically. You interact through a web dashboard or the AI agent—never through a terminal. If you can push code to GitHub, you can use DollarDeploy."
  },
  {
    question: "Will my app be good for SEO and AI agents?",
    answer:
      "Yes. Unlike serverless platforms with cold starts and edge-function quirks, your app runs as a persistent service on a real server. That means consistent response times, proper server-side rendering, no cold-start delays for crawlers, and full control over headers, robots.txt, and sitemaps. Your app responds the same way to users, search engine bots, and AI agents."
  },
  {
    question: "Which cloud providers can I use?",
    answer:
      "Any Linux server with SSH access works. We have one-click provisioning for **DigitalOcean, Hetzner, Verda Cloud (GPU servers), and AWS**. You can also bring your own VPS, bare-metal server, or even a Raspberry Pi. We treat cloud as a utility—pick the cheapest or best provider today, and switch tomorrow without changing your workflow."
  },
  {
    question: "Can I switch cloud providers later?",
    answer:
      "Yes, and that's the point. Since DollarDeploy uses standard primitives—a Linux VPS, Postgres, object storage—there's no proprietary runtime or platform-specific config locking you in. Provision a new server on a different provider, redeploy your apps, and you're done. Same dashboard, same workflow."
  },
  {
    question: "How does native execution work (no Docker)?",
    answer:
      "DollarDeploy builds your app on our infrastructure and deploys the result directly to your server as a **systemd service**—the same way production software runs on Linux. No Docker containers, no registry overhead, no YAML files. Databases like PostgreSQL and Redis are also installed as native OS packages. The result is better performance on cheaper hardware."
  },
  {
    question: "How are HTTPS certificates handled?",
    answer:
      "Automatically. Point your domain to your server, add it in the dashboard, and we obtain and renew free **Let's Encrypt SSL certificates** with zero manual steps. Certificates renew before they expire, so you never think about HTTPS again."
  },
  {
    question: "How do backups work?",
    answer:
      "One click or on a schedule. Back up your databases and application files directly from the dashboard to **S3-compatible storage**. You can configure backups per app or per server, and restore when needed. No scripts to write, no cron jobs to maintain."
  },
  {
    question: "Can I deploy AI models or LLMs?",
    answer:
      "Yes. Provision **GPU-enabled servers from Verda Cloud (formerly DataCrunch)** directly from the dashboard, and deploy LLMs like DeepSeek or open-source models in one click. DollarDeploy handles the server setup so you can focus on the model, not the infrastructure."
  },
  {
    question: "Is my data secure? Who has access to my server?",
    answer:
      "Your code, data, and server remain under your control. DollarDeploy only uses SSH access to manage deployments and configurations—we don't store your application data. SSH keys, API tokens, and credentials are **encrypted at rest** in our database. You can revoke access at any time."
  },
  {
    question: "How is this different from Coolify or Dokploy?",
    answer:
      "Coolify and Dokploy are open-source self-hosted PaaS tools—they're powerful, but they require Docker knowledge, terminal comfort, and you have to host and maintain the platform itself. DollarDeploy runs apps natively (no Docker needed), includes a built-in AI agent that handles infrastructure, and is a managed service so there's nothing for you to self-host or update."
  }
];

export const FAQ = ({ limit }: { limit?: number }) => {
  const list = limit !== undefined ? faqs.slice(0, limit) : faqs;

  return (
    <section id="faq" className="container">
      <h2 className="text-3xl md:text-4xl font-bold mb-4">
        Frequently Asked <span className="gradient-light dark:gradient-dark">Questions</span>
      </h2>

      <Accordion type="single" collapsible className="w-full AccordionRoot">
        {list.map(({ question, answer }, index) => (
          <AccordionItem key={index} value={String(index)}>
            <AccordionTrigger className="text-left font-semibold">{question}</AccordionTrigger>

            <AccordionContent className="text-base">{formatMarkdown(answer)}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="flex md:flex-row flex-col gap-4 justify-between items-center mt-8">
        {limit !== undefined ? (
          <Link href="/faq" className="link">
            See all the FAQs →
          </Link>
        ) : (
          <span />
        )}

        <h3 className="font-medium">
          Still have questions?{" "}
          <ExternalLink href="mailto:hello@dollardeploy.com" className="link" target="_self">
            Contact us
          </ExternalLink>{" "}
          or chat with us on{" "}
          <ExternalLink
            href="https://discord.gg/BHbfmaAGb7"
            target="_blank"
            rel="noreferrer noopener"
            className="link"
          >
            Discord
          </ExternalLink>
          .
        </h3>
      </div>
    </section>
  );
};
